import { useCallback, useState } from "react";
import { exportToCSV } from "@/lib/csv-export";
import type { UnifiedTableColumn } from "@/types/table-column";

/**
 * Common props shared between all CSV export hooks
 */
interface BaseCSVExportProps<T> {
  /**
   * Columns to export
   */
  columns: UnifiedTableColumn<T>[];

  /**
   * Filename for the exported CSV
   */
  filename: string;

  /**
   * Optional callback to execute before export
   */
  onBeforeExport?: () => void;

  /**
   * Optional callback to execute after export
   */
  onAfterExport?: () => void;

  /**
   * Optional callback for export errors
   */
  onError?: (error: Error) => void;
}

interface CSVExportOptions<T> extends BaseCSVExportProps<T> {
  /**
   * Data to export, either an array or a function that returns a promise of an array
   */
  getData: T[] | (() => Promise<T[]>);
}

/**
 * Hook to provide CSV export functionality to any component
 */
export function useCSVExport<T>({
  getData,
  columns,
  filename,
  onBeforeExport,
  onAfterExport,
  onError,
}: CSVExportOptions<T>) {
  const [isExporting, setIsExporting] = useState(false);

  const exportData = useCallback(
    async (customFilename?: string) => {
      if (isExporting) return;

      try {
        setIsExporting(true);
        onBeforeExport?.();

        // Filter out non-exportable columns
        const exportableColumns = columns.filter(
          (col) => col.exportable !== false,
        );
        if (exportableColumns.length === 0) {
          throw new Error("No exportable columns available in the data");
        }

        // Get the data (either from array or from function)
        const data = typeof getData === "function" ? await getData() : getData;

        // Use custom filename if provided and it's a valid string, otherwise use default
        // This handles cases where onClick={exportData} passes the event object
        const finalFilename =
          customFilename && typeof customFilename === "string"
            ? customFilename
            : filename || "export";

        // Export the data using only exportable columns
        await exportToCSV(data, exportableColumns, finalFilename);

        onAfterExport?.();
      } catch (error) {
        console.error("Error exporting CSV:", error);
        onError?.(error as Error);
      } finally {
        setIsExporting(false);
      }
    },
    [
      getData,
      columns,
      filename,
      onBeforeExport,
      onAfterExport,
      onError,
      isExporting,
    ],
  );

  return {
    isExporting,
    exportData,
    defaultFilename: filename,
  };
}

/**
 * Hook specifically for exporting table data that already exists in state
 */
export function useTableCSVExport<T>({
  data,
  columns,
  filename,
  onBeforeExport,
  onAfterExport,
  onError,
}: BaseCSVExportProps<T> & { data: T[] }) {
  return useCSVExport<T>({
    getData: data,
    columns,
    filename,
    onBeforeExport,
    onAfterExport,
    onError,
  });
}

/**
 * Hook for exporting data from paginated APIs
 */
export function usePaginatedCSVExport<T>({
  fetchAllData,
  columns,
  filename,
  onBeforeExport,
  onAfterExport,
  onError,
}: BaseCSVExportProps<T> & { fetchAllData: () => Promise<T[]> }) {
  return useCSVExport<T>({
    getData: fetchAllData,
    columns,
    filename,
    onBeforeExport,
    onAfterExport,
    onError,
  });
}
